import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit';
import { RootState } from 'store/store';

import { DocumentType } from 'app/types/document-type';
import { AsyncSelectOptions } from 'app/types/select-options';

import { UnidentifiedDrivingEvents } from 'app/services/api/models/UnidentifiedDrivingEvents';

import {
  ExportFileData,
  DecoratedUnidentifiedDrivingEvents,
  UnidentifiedDrivingState,
} from 'app/pages/hos/UnidentifiedDriving/types';

import { unidentifiedDrivingState } from './unidentifiedDrivingState';

export const unidentifiedDrivingSlice = createSlice({
  name: 'driverStatus',
  initialState: unidentifiedDrivingState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    // ---------------------------------------------------------
    // DATA FILTERS
    // ---------------------------------------------------------
    setVehicle: (state, action: PayloadAction<AsyncSelectOptions | null>) => {
      state.vehicle = action.payload;
    },
    setTerminal: (state, action: PayloadAction<AsyncSelectOptions | null>) => {
      state.terminal = action.payload;
    },
    setDateRange: (state, action: PayloadAction<Array<string>>) => {
      state.dateRange = action.payload;
    },
    setUnassignedUnid: (state, action: PayloadAction<boolean>) => {
      state.isUnassigned = action.payload;
    },
    setClassifiedUnid: (state, action: PayloadAction<boolean>) => {
      state.isClassified = action.payload;
    },
    // ---------------------------------------------------------
    // SORTING
    // ---------------------------------------------------------
    setSortBy: (
      state,
      action: PayloadAction<UnidentifiedDrivingState['sortBy']>,
    ) => {
      state.sortBy = action.payload;
    },
    setSortField: (
      state,
      action: PayloadAction<UnidentifiedDrivingState['sortField']>,
    ) => {
      state.sortField = action.payload;
    },
    // ---------------------------------------------------------
    // FILE EXPORT DOCUMENT TYPE
    // ---------------------------------------------------------
    setDocumentType: (state, action: PayloadAction<DocumentType>) => {
      state.documentType = action.payload;
    },
    // ---------------------------------------------------------
    // MODAL TOGGLERS, SHOW/HIDE MODAL(S)
    // ---------------------------------------------------------
    setToggleExportFileModal: (state, action: PayloadAction<boolean>) => {
      state.isToggleExportFileModal = action.payload;
    },
    setToggleAssignUnidDrivingModal: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.isToggleAssignUnidDrivingModal = action.payload;
    },
    setToggleBulkAssignUnidDrivingModal: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.isToggleBulkAssignUnidDrivingModal = action.payload;
    },
    // ---------------------------------------------------------
    // API REQUEST STATUS
    // ---------------------------------------------------------
    setInitializeFetchDataRequestPendingTo: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.isInitializeFetchDataRequestPending = action.payload;
    },
    setInitializeFileExportFetchRequestPendingTo: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.isInitializeFileExportRequestPending = action.payload;
    },
    // ---------------------------------------------------------
    // PAGINATION
    // ---------------------------------------------------------
    setLimit: (state, action: PayloadAction<number>) => {
      state.limit = action.payload;
    },
    setPageIndex: (state, action: PayloadAction<number>) => {
      state.pageIndex = action.payload;
    },
    setTotalRecordCounts: (state, action: PayloadAction<number>) => {
      state.totalRecordCount = action.payload;
    },
    // ---------------------------------------------------------
    // DATA SOURCE
    // ---------------------------------------------------------
    setExportFileData: (
      state,
      action: PayloadAction<Array<ExportFileData>>,
    ) => {
      state.exportFileData = action.payload;
    },
    setSelectedUnidEvents: (
      state,
      action: PayloadAction<Array<DecoratedUnidentifiedDrivingEvents>>,
    ) => {
      state.selectedUnidEvents = action.payload;
    },
    setRefreshDataCount: (state, action: PayloadAction<number>) => {
      state.refreshDataCount = action.payload;
    },
    setUnidentifiedDrivingEvents: (
      state,
      action: PayloadAction<Array<UnidentifiedDrivingEvents>>,
    ) => {
      state.unidentifiedDrivingEvents = action.payload;
    },
    setDecoratedUnidentifiedDrivingEvents: (
      state,
      action: PayloadAction<Array<DecoratedUnidentifiedDrivingEvents>>,
    ) => {
      state.decoratedUnidentifiedDrivingEvents = action.payload;
    },
    setDataReset: () => unidentifiedDrivingState,
  },
});

/* ------- ACTION CREATORS ------- */
export const { actions: unidentifiedDrivingActions } = unidentifiedDrivingSlice;

/* ------- STATE SELECTORS ------- */
export const selectUnidentifiedDriving = (state: RootState) =>
  state.unidentifiedDriving || unidentifiedDrivingState;

// Data Filters
export const makeSelectVehicle = () =>
  createSelector(
    selectUnidentifiedDriving,
    selectUnidentifiedDriving => selectUnidentifiedDriving.vehicle,
  );

export const makeSelectTerminal = () =>
  createSelector(
    selectUnidentifiedDriving,
    selectUnidentifiedDriving => selectUnidentifiedDriving.terminal,
  );

export const makeSelectDateRange = () =>
  createSelector(
    selectUnidentifiedDriving,
    selectUnidentifiedDriving => selectUnidentifiedDriving.dateRange,
  );

export const makeSelectUnassignedUnid = () =>
  createSelector(
    selectUnidentifiedDriving,
    selectUnidentifiedDriving => selectUnidentifiedDriving.isUnassigned,
  );

export const makeSelectUnassignedClassified = () =>
  createSelector(
    selectUnidentifiedDriving,
    selectUnidentifiedDriving => selectUnidentifiedDriving.isClassified,
  );

// Sorting
export const makeSelectSortBy = () =>
  createSelector(
    selectUnidentifiedDriving,
    selectUnidentifiedDriving => selectUnidentifiedDriving.sortBy,
  );

export const makeSelectSortField = () =>
  createSelector(
    selectUnidentifiedDriving,
    selectUnidentifiedDriving => selectUnidentifiedDriving.sortField,
  );

// File Export Document Type
export const makeSelectDocumentType = () =>
  createSelector(
    selectUnidentifiedDriving,
    selectUnidentifiedDriving => selectUnidentifiedDriving.documentType,
  );

// Modal Togglers
export const makeSelectToggleExportFileModal = () =>
  createSelector(
    selectUnidentifiedDriving,
    selectUnidentifiedDriving =>
      selectUnidentifiedDriving.isToggleExportFileModal,
  );

export const makeSelectToggleAssignUnidDrivingModal = () =>
  createSelector(
    selectUnidentifiedDriving,
    selectUnidentifiedDriving =>
      selectUnidentifiedDriving.isToggleAssignUnidDrivingModal,
  );

export const makeSelectToggleBulkAssignUnidDrivingModal = () =>
  createSelector(
    selectUnidentifiedDriving,
    selectUnidentifiedDriving =>
      selectUnidentifiedDriving.isToggleBulkAssignUnidDrivingModal,
  );

// API Request Status
export const makeSelectInitializeFetchDataRequestPending = () =>
  createSelector(
    selectUnidentifiedDriving,
    selectUnidentifiedDriving =>
      selectUnidentifiedDriving.isInitializeFetchDataRequestPending,
  );
export const makeSelectInitializeFileExportFetchDataRequestPending = () =>
  createSelector(
    selectUnidentifiedDriving,
    selectUnidentifiedDriving =>
      selectUnidentifiedDriving.isInitializeFileExportRequestPending,
  );

// Pagination
export const makeSelectLimit = () =>
  createSelector(
    selectUnidentifiedDriving,
    selectUnidentifiedDriving => selectUnidentifiedDriving.limit,
  );

export const makeSelectPageIndex = () =>
  createSelector(
    selectUnidentifiedDriving,
    selectUnidentifiedDriving => selectUnidentifiedDriving.pageIndex,
  );

export const makeSelectTotalRecordCount = () =>
  createSelector(
    selectUnidentifiedDriving,
    selectUnidentifiedDriving => selectUnidentifiedDriving.totalRecordCount,
  );

// Data Source
export const makeSelectExportFileData = () =>
  createSelector(
    selectUnidentifiedDriving,
    selectUnidentifiedDriving => selectUnidentifiedDriving.exportFileData,
  );
export const makeSelectSelectedUnidEvents = () =>
  createSelector(
    selectUnidentifiedDriving,
    selectUnidentifiedDriving => selectUnidentifiedDriving.selectedUnidEvents,
  );
export const makeSelectRefreshDataCount = () =>
  createSelector(
    selectUnidentifiedDriving,
    selectUnidentifiedDriving => selectUnidentifiedDriving.refreshDataCount,
  );
export const makeSelectUnidentifiedDrivingEvents = () =>
  createSelector(
    selectUnidentifiedDriving,
    selectUnidentifiedDriving =>
      selectUnidentifiedDriving.unidentifiedDrivingEvents,
  );
export const makeSelectDecoratedUnidentifiedDrivingEvents = () =>
  createSelector(
    selectUnidentifiedDriving,
    selectUnidentifiedDriving =>
      selectUnidentifiedDriving.decoratedUnidentifiedDrivingEvents,
  );

export default unidentifiedDrivingSlice.reducer;
